// 桌面壳（Electron）平台适配层：统一收口对 preload 注入桥（window.maxusDesktop）的访问。
// 业务代码不直接读 window.maxusDesktop，一律经此模块；桥缺失（website 目标）时优雅降级为 no-op。
// 平台判定用编译期 VITE_APP_TARGET，禁止运行时探测（见包根 README.md“运行时目标”）。
import { env } from '@maxus/website/constants'

// loopback 回传端口：须与 electron/main 的 DESKTOP_CALLBACK_PORT 同步（改端口需两处同步）。
// ticket 回传改走 loopback 本地 HTTP——macOS dev 下自定义协议深链接不可用（见 electron/main startLoopbackServer）。
const DESKTOP_CALLBACK_PORT = 52847

// 落地页把一次性 ticket 回传桌面壳的 loopback URL；主进程 loopback server 收到后走 IPC 换 token。
const buildDesktopCallbackUrl = (ticket: string): string =>
  `http://127.0.0.1:${DESKTOP_CALLBACK_PORT}/auth-callback?ticket=${encodeURIComponent(ticket)}`

// 是否为 Electron 桌面壳构建目标（编译期静态值）。
export const isDesktopTarget = env.VITE_APP_TARGET === 'electron'

export interface DesktopLibmpvBounds {
  x: number
  y: number
  width: number
  height: number
}

export interface DesktopWindowButtonPosition {
  x: number
  y: number
}

export type DesktopLibmpvEvent =
  | { type: 'play' }
  | { type: 'playing' }
  | { type: 'pause' }
  | { type: 'waiting' }
  | { type: 'ended' }
  | { type: 'time-update'; currentTime: number }
  | { type: 'duration-change'; duration: number }
  | { type: 'volume-change'; volume: number; muted: boolean }
  | { type: 'loaded-metadata'; width: number; height: number }
  | { type: 'error'; message: string }

export interface DesktopLibmpvBridge {
  create: (playerId: string, bounds: DesktopLibmpvBounds, url: string) => Promise<void>
  destroy: (playerId: string) => void
  load: (playerId: string, url: string) => Promise<void>
  pause: (playerId: string) => void
  play: (playerId: string) => void
  seek: (playerId: string, time: number) => void
  setBounds: (playerId: string, bounds: DesktopLibmpvBounds) => void
  setMuted: (playerId: string, muted: boolean) => void
  setPlaybackRate: (playerId: string, rate: number) => void
  setVolume: (playerId: string, volume: number) => void
  onEvent: (handler: (playerId: string, event: DesktopLibmpvEvent) => void) => () => void
}

// preload 通过 contextBridge 注入的受控桥。website 目标下为 undefined。
export interface MaxusDesktopBridge {
  version: string
  copyText: (text: string) => Promise<boolean>
  // 用系统默认浏览器打开外链（登录走外部浏览器完成飞书 SSO）。
  openExternal: (url: string) => void
  // 订阅主进程经深链接回传的登录 ticket（maxus://auth-callback?ticket=...）。返回取消订阅函数。
  onAuthCallback: (handler: (ticket: string) => void) => () => void
  setFullscreen: (fullscreen: boolean) => void
  onFullscreenChange: (handler: (fullscreen: boolean) => void) => () => void
  // 仅 Electron 开发态暴露，用于在 DevTools 中实时调整 macOS 原生窗口按钮的位置。
  setWindowButtonPosition?: (position: DesktopWindowButtonPosition) => void
  // macOS 原生 libmpv 播放器。只暴露 PlayerHandle 所需的白名单能力。
  libmpv: DesktopLibmpvBridge
}

declare global {
  interface Window {
    maxusDesktop?: MaxusDesktopBridge
  }
}

const getBridge = (): MaxusDesktopBridge | undefined =>
  typeof window !== 'undefined' ? window.maxusDesktop : undefined

export const getDesktopLibmpvBridge = (): DesktopLibmpvBridge | undefined => getBridge()?.libmpv

export const copyDesktopText = async (text: string): Promise<boolean> => {
  const bridge = getBridge()
  if (!bridge) return false
  try {
    return await bridge.copyText(text)
  } catch {
    return false
  }
}

export const setDesktopFullscreen = (fullscreen: boolean): void => {
  getBridge()?.setFullscreen(fullscreen)
}

export const onDesktopFullscreenChange = (handler: (fullscreen: boolean) => void): (() => void) =>
  getBridge()?.onFullscreenChange(handler) ?? (() => {})

// 构造外部浏览器打开的飞书登录 URL：绝对地址（见 VITE_DESKTOP_LOGIN_BASE_URL 注释），
// 带 client=electron 让后端在回跳 URL 上回显 desktop=1，落地页据此改走深链接回传。
export const buildDesktopLoginUrl = (): string => {
  const base = env.VITE_DESKTOP_LOGIN_BASE_URL || 'http://localhost:5173/api'
  return `${base}/auth/feishu/login?redirect=%2F&client=electron`
}

// 打开外部浏览器登录。桥缺失（非桌面壳 / 注入失败）时返回 false，由调用方降级处理。
export const openDesktopLogin = (): boolean => {
  const bridge = getBridge()
  if (!bridge) return false
  bridge.openExternal(buildDesktopLoginUrl())
  return true
}

// 订阅深链接回传的登录 ticket；桥缺失时返回 no-op 取消函数（website 目标安全空转）。
export const onDesktopAuthCallback = (handler: (ticket: string) => void): (() => void) => {
  const bridge = getBridge()
  if (!bridge) return () => {}
  return bridge.onAuthCallback(handler)
}

// 打开外链 / 自定义协议（如飞书 applink）。桌面壳交给主进程用系统默认程序打开，
// 否则会在渲染进程内导航掉整个应用；website 目标回落新开标签页。
export const openExternalUrl = (url: string): void => {
  const bridge = getBridge()
  if (bridge) {
    bridge.openExternal(url)
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}

// 落地页（外部浏览器，website 构建）把一次性 ticket 经 loopback 顶层跳转回传桌面壳。
// 用顶层导航而非后台 fetch/img：https 落地页对 http://127.0.0.1 发子资源请求会踩 mixed-content / PNA 预检，
// 而顶层导航恒被允许（这也是 RFC 8252 Native App OAuth 的 redirect 方式）。
export const relayTicketToDesktop = (ticket: string): void => {
  window.location.href = buildDesktopCallbackUrl(ticket)
}
